/**
 * @fileOverview The frame: which route it is on, loading it, and getting it to another view the way a
 * person would — by walking the pointer to the tab and clicking it.
 */

import router from "@/router"
import { logger } from "@/services/logger"
import { isSealedViewHelpWindow, viewHelpEmbedUrl } from "@/services/viewHelp/embed"
import { aimForControl } from "@/services/viewHelp/pointer"
import {
  anchorRejection,
  isAnchorVisible,
  scrollAnchorIntoView,
  scrollScrollerToTop,
  waitForStableRect,
} from "@/services/viewHelp/target"
import { wayTo } from "@/services/viewHelp/ways"
import { hoverOver, isLink } from "@/services/viewHelp/hover"
import {
  FRAME_LOAD_TIMEOUT_MS,
  HOVER_MS,
  IN_FRAME_NAV_TIMEOUT_MS,
  LINK_HOVER_MS,
  PRESS_HOLD_MS,
  SCROLL_MS,
  WAY_TARGET_TIMEOUT_MS,
  ZOOM_MS,
  sleep,
} from "./timing"
import {
  Move,
  cameraMs,
  focusRect,
  frameLoadTarget,
  frameLoads,
  frameRoute,
  frameWindow,
  isFrameLoading,
  motionMs,
  pressPointer,
  scaled,
  type FrameTarget,
} from "./state"
import { aimCamera, holdOnPage, isZoomedIn, releaseHold } from "./camera"
import { bringPointerNear, flyPointerTo } from "./pointer"
import { dispatchMouse } from "./input"
import { forgetPlayedScenes, forgetScene } from "./scenes"
import { forgetPins, runActions } from "./actions"
import { tour } from "./tour"

/** Resolves when the frame reports it has loaded the route it was pointed at. */
let frameReady: (() => void) | null = null

/**
 * Settles the load that is being waited for, as failed — for a newer load that replaces it. Without it
 * the replaced wait ran on to its `FRAME_LOAD_TIMEOUT_MS`, and the move waiting on it with it: eight
 * seconds in which the next move, which waits for the overtaken one to wind down, could not start.
 */
let settleReplacedLoad: (() => void) | null = null

/**
 * Whether the current load has already been sent back once for arriving unsealed. A document whose
 * scripts failed to load (the network dropping mid-load) never seals itself either; sending it back
 * again and again would reload the frame for as long as the network stays down. Once is the check;
 * after that the load counts as failed.
 */
let sentBackUnsealed = false

/**
 * The route the frame is actually on, read from its own URL.
 *
 * Remembering it is not enough once moves can overtake each other: a walk to a tab that is
 * overtaken *after* its click has already navigated the frame, and a remembered route would still
 * name the page it left. Everything that asks "is the frame already there?" asks the frame.
 */
export function actualFrameRoute(): string | null {
  const win = frameWindow.value
  if (!win) return frameRoute.value
  try {
    const name = router.resolve(win.location.pathname).name
    if (typeof name === "string") frameRoute.value = name
  } catch {
    // A frame mid-navigation can briefly refuse; the last known route is the best answer then.
  }
  return frameRoute.value
}

/**
 * Called by `LewViewHelpStage` when the frame has finished loading a route.
 *
 * Every load is checked before the window is handed to the player. A full document load the app makes
 * on its own — the chunk-error reload, the release swap — once went to the bare path and brought the
 * frame back as the unsealed app, signed in as the reader, with the pointer about to drive it; its
 * first read went to the real API. The frame's `name` now marks any instance in it as embedded from its
 * first line (`VIEW_HELP_FRAME_NAME`), and `isolation.ts` seals it and leaves a mark. This checks the
 * mark, not the name: an instance that did not seal itself — whatever the reason — is never driven. The
 * frame is sent back to the same address with the flag, and the player waits for that load instead.
 */
export function onFrameLoaded(win: Window | null) {
  // `about:blank` is the empty frame before its first route, not an app instance.
  if (win && win.location.href !== "about:blank" && !isSealedViewHelpWindow(win)) {
    frameWindow.value = null
    if (!sentBackUnsealed) {
      sentBackUnsealed = true
      logger.log(`View help frame loaded unsealed at ${win.location.pathname} — sent back`)
      if (import.meta.env.DEV) console.warn(`[view-help] frame loaded unsealed at ${win.location.pathname} — reloading`)
      isFrameLoading.value = true
      const { pathname, search, hash } = win.location
      win.location.replace(viewHelpEmbedUrl(`${pathname}${search}${hash}`))
      return
    }
    // Unsealed twice: never driven. The load is over and has failed; whoever waited for it finds no window.
    logger.log(`View help frame loaded unsealed again at ${win.location.pathname} — given up`)
    isFrameLoading.value = false
    frameReady?.()
    return
  }
  sentBackUnsealed = false
  frameWindow.value = win
  isFrameLoading.value = false
  frameReady?.()
}

/** Forgets a load that was waited for — for a close, which throws the frame away. */
export function forgetFrame() {
  frameReady = null
}

/**
 * Reloads the frame onto a route. How the walkthrough opens — there is no app instance yet to click
 * through — and how it recovers when a view has no tab to click. It is the one place a spinner is
 * shown, because it is the one place there is genuinely nothing to look at yet.
 */
export function reloadFrameOnto(target: FrameTarget, force = false): Promise<boolean> {
  const win = frameWindow.value
  // `force`: a fresh instance even on the route the frame is already on — for an irreversible scene.
  if (!force && win && isFrameOn(win, target)) return Promise.resolve(true)

  settleReplacedLoad?.()

  return new Promise(resolve => {
    let settled = false
    const finish = (ok: boolean) => {
      if (settled) return
      settled = true
      if (settleReplacedLoad === replaced) settleReplacedLoad = null
      resolve(ok)
    }
    const replaced = () => finish(false)
    settleReplacedLoad = replaced

    sentBackUnsealed = false
    frameReady = () => {
      frameReady = null
      isFrameLoading.value = false
      // A load given up on (see `onFrameLoaded`) reports ready with no window: that is a failed load.
      finish(Boolean(frameWindow.value))
    }
    // A new app instance, so whatever scene the old one was in is gone with it — and every page state
    // an irreversible scene left behind.
    forgetScene()
    forgetPlayedScenes()
    forgetPins()
    isFrameLoading.value = true
    focusRect.value = null
    frameRoute.value = target.name
    frameLoadTarget.value = target
    frameLoads.value += 1
    frameWindow.value = null
    setTimeout(() => {
      if (settled) return
      frameReady = null
      isFrameLoading.value = false
      finish(Boolean(frameWindow.value))
    }, FRAME_LOAD_TIMEOUT_MS)
  })
}

/**
 * Whether the frame is on `target` right now, read from its own URL.
 *
 * Not from the target it was last *loaded* onto: the app inside navigates itself — every walk to a
 * tab, every `way` — and that target then names a page the frame left long ago. Asked that, going back
 * to the view the walkthrough opened on answered "already there", reloaded nothing, and every step of
 * that view waited out its anchors on the wrong page and was skipped: the screen stood still, and the
 * step never came.
 */
function isFrameOn(win: Window, target: FrameTarget): boolean {
  try {
    const { pathname, hash, search } = win.location
    if (pathname !== router.resolve({ name: target.name, params: target.params }).path) return false
    if (target.hash && hash !== `#${target.hash}`) return false
    const query = new URLSearchParams(search)
    return Object.entries(target.query ?? {}).every(([key, value]) => query.get(key) === value)
  } catch {
    return false
  }
}

/** Resolves on the frame's next paint — or shortly after, if a throttled frame never paints. */
const nextFrame = (win: Window) =>
  Promise.race([new Promise<void>(resolve => win.requestAnimationFrame(() => resolve())), sleep(60)])

/**
 * Waits until the app inside the frame has really navigated to `name`, then two frames for the new
 * view to render.
 *
 * The URL is the signal, not the view's anchors: several anchors live on every page of the feature
 * (the tab strip is on all six), so "the anchor is there" would be true of the page being *left*
 * as well. vue-router resolves a lazy route before it updates the URL, so by the time the path has
 * changed the new view only has to render.
 */
async function waitForFrameRoute(win: Window, target: FrameTarget, timeoutMs: number, move: Move): Promise<boolean> {
  const path = router.resolve({ name: target.name, params: target.params }).path
  const deadline = performance.now() + timeoutMs
  while (win.location.pathname !== path) {
    if (performance.now() >= deadline || !move.current) return false
    await sleep(16)
  }
  await nextFrame(win)
  await nextFrame(win)
  return true
}

/** Says, loudly in development, why a hop had to reload rather than be walked. */
function warnFallback(name: string, reason: string) {
  const detail = `walking to "${name}" fell back to a reload: ${reason}`
  logger.log(`View help ${detail}`)
  if (import.meta.env.DEV) console.warn(`[view-help] ${detail}`)
}

/** Waits for the frame to arrive on `target` after a click. False, and says why, if it does not. */
async function arriveOn(win: Window, target: FrameTarget, move: Move, clicked = "its tab"): Promise<boolean> {
  if (!(await waitForFrameRoute(win, target, IN_FRAME_NAV_TIMEOUT_MS, move))) {
    if (move.current) warnFallback(target.name, `clicking ${clicked} did not navigate`)
    return false
  }
  if (!move.current) return false
  frameRoute.value = target.name
  return true
}

/**
 * Pulls the camera out to the whole page, for the pointer to walk somewhere with everything in shot.
 * False when the move was overtaken on the way.
 */
async function stepBack(move: Move): Promise<boolean> {
  if (isZoomedIn()) {
    aimCamera(null, motionMs(ZOOM_MS))
    return move.pause(cameraMs.value)
  }
  focusRect.value = null
  return true
}

/**
 * Gets to a route the way a person would.
 *
 *   1. **Step back.** The camera pulls out to the whole page, then the page scrolls all the way back
 *      to its top. The step before a hop is often low on the page — Meine Zeiten ends in its
 *      right-hand column — which had scrolled the strip out of view; and scrolled back only as far
 *      as the strip, the strip sat against the frame's top edge, where the press on it could hardly
 *      be seen. From the top of the page the strip is in the middle of what the reader is looking at.
 *   2. **Walk.** The pointer travels to the tab with the whole page in shot, so the reader sees
 *      where it is going the entire way.
 *   3. **Press.** It rests on the tab for a beat, then presses: our ripple on the pointer, and the
 *      tab's own Material ripple under it.
 *   4. **Arrive.** The app inside the frame routes itself, as an ordinary SPA transition.
 *
 * That is the answer to "no surprise navigations": the page changing is something the reader watched
 * happen and could repeat. It is also gentler than a reload — the frame keeps its app instance, so
 * the sample week survives and nothing is re-fetched.
 *
 * An earlier version skipped step 1, found the strip scrolled out of view, and quietly reloaded the
 * frame instead — which looked like a rough transition rather than like a broken one. Every way this
 * can now fall back says why, in the console, in development.
 *
 * Only ever a tab, reached through `data-help-route`. A press on "Einstempeln" would fire a real
 * clock-in inside the frame — refused by the sandbox, but shown to the reader as a failure.
 */
async function clickThroughToRoute(name: string, move: Move, walk: boolean): Promise<boolean> {
  const win = frameWindow.value
  if (!win) return false

  const tab = win.document.querySelector<HTMLElement>(`[data-help-route="${CSS.escape(name)}"]`)
  // No strip at all — somebody with a single destination. A reason to reload, not a fault.
  if (!tab) return false

  // A seek goes straight there. It is still the app's own navigation — no reload, no white flash —
  // only without the walk: a `.click()` needs no scrolling and no pointer to reach the tab.
  if (!walk) {
    tab.click()
    return arriveOn(win, { name }, move)
  }

  // 1. Step back: the camera pulls out to the whole page first, and only then does the page scroll up
  // to the strip. Both at once moved the picture two ways — zooming, and sliding vertically inside
  // the zoom — which read as the zoom going one way across and another way down.
  if (!(await stepBack(move))) return false
  await scrollScrollerToTop(win, tab, motionMs(SCROLL_MS), () => !move.current)
  if (!move.current) return false
  // The strip is near the top of every page; this only matters for one that is not.
  await scrollAnchorIntoView(win, tab, 0, () => !move.current)
  if (!move.current) return false
  await waitForStableRect(win, tab)
  if (!isAnchorVisible(win, tab)) {
    warnFallback(name, `its tab is not on screen (${anchorRejection(win, tab)})`)
    return false
  }

  // 2. Walk.
  const target = aimForControl(tab.getBoundingClientRect())
  bringPointerNear(target)
  await flyPointerTo(target, move)
  if (!move.current) return false
  hoverOver(tab)
  if (!(await move.pause(motionMs(HOVER_MS + (isLink(tab) ? LINK_HOVER_MS : 0))))) return false

  // 3. Press.
  pressPointer()
  dispatchMouse(win, tab, "mousedown", target)
  if (!(await move.pause(scaled(PRESS_HOLD_MS)))) return false
  dispatchMouse(win, tab, "mouseup", target)
  // A real click on the real `<a>` the tab renders, so the app's own router handles it. The frame is
  // `pointer-events: none` for the reader, which a dispatched event is not subject to.
  tab.click()

  // 4. Arrive.
  return arriveOn(win, { name }, move)
}

/**
 * Walks the way to a view — the drawer entry, a row in a list, the page's own tab — one click after
 * another, each on the page the one before it opened. The shared way from `services/viewHelp/ways.ts`,
 * or the tour's own where it has one.
 *
 * The camera stays on the whole page for all of it (a hold on the whole frame, the way a `zoom: false`
 * step works), so the reader sees where the pointer goes rather than the camera diving onto each thing
 * it clicks. A seek clicks the same way at speed: still the app's own navigation, never a reload.
 * False, and says why, when a click found nothing or did not lead there.
 */
async function walkTheWay(target: FrameTarget, move: Move, walk: boolean): Promise<boolean> {
  const way = wayTo(tour.value, target.name, target, actualFrameRoute())
  const win = frameWindow.value
  if (!way.length || !win) return false

  if (walk) {
    if (!(await stepBack(move))) return false
    holdOnPage()
  }
  let walked: boolean
  try {
    walked = await runActions(way, move, walk, WAY_TARGET_TIMEOUT_MS)
  } finally {
    releaseHold()
  }
  if (!move.current) return false
  if (!walked) {
    warnFallback(target.name, "a click on its way there found nothing to click")
    return false
  }
  return arriveOn(win, target, move, "the way there")
}

/**
 * Whichever way works: walk to the tab if there is one; otherwise walk the way there (`walkTheWay`);
 * reload if neither gets there. `walk: false` — a seek — clicks the same things without the walk.
 *
 * A move that has been overtaken — the reader clicked the timeline mid-walk — must not reload the
 * frame on its way out, or it would yank the page from under the move that replaced it.
 */
export async function showRouteInFrame(target: FrameTarget, move: Move, walk: boolean): Promise<boolean> {
  if (actualFrameRoute() === target.name && frameWindow.value) return true
  if (await clickThroughToRoute(target.name, move, walk)) return true
  if (!move.current) return false
  if (await walkTheWay(target, move, walk)) return true
  if (!move.current) return false
  return reloadFrameOnto(target)
}
